/**
 * Get the actual header height with fallback values
 */
function getHeaderHeight() {
	// Try to get the header element directly
	const headerElement = document.querySelector('.site-header');
	if (headerElement) {
		return headerElement.offsetHeight;
	}

	// Fallback to CSS variable
	const cssVarValue = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
	if (cssVarValue) {
		const parsed = parseInt(cssVarValue);
		if (!isNaN(parsed) && parsed > 0) {
			return parsed;
		}
	}

	// Final fallback
	return 100;
}

/**
 * Open an FAQ item if it's closed
 */
function openFaqItem(faqItem) {
	if (!faqItem) return;

	const question = faqItem.querySelector('.rank-math-question');
	if (!question) return;

	// Initialize the toggled state if not set
	if (typeof question.dataset.toggled === 'undefined') {
		question.dataset.toggled = '0';
	}

	// Open the FAQ if it's not already open
	if (question.dataset.toggled === '0') {
		// Add active class to parent
		faqItem.classList.add('rank-math-list-item--active');

		// Open the answer
		const answer = question.nextElementSibling;
		if (answer) {
			answer.removeAttribute('hidden');
			answer.style.height = answer.scrollHeight + 20 + 'px';
		}

		// Update toggle state
		question.dataset.toggled = '1';
	}
}

/**
 * Switch to the tab containing the target element
 */
function switchToTabContainingElement(targetEl) {
	if (!targetEl) return false;

	// Check if element is within a tab panel
	const panel = targetEl.closest('[role="tabpanel"], .tab-panel');
	if (!panel) return false;

	// Find the tabs wrapper
	const tabsWrapper = panel.closest('[data-farlo-ui-faq], .farlo-ui-tabs');
	if (!tabsWrapper) return false;

	// Find all tabs and panels
	const tabs = Array.from(tabsWrapper.querySelectorAll('[data-farlo-ui-tab], [role="tab"], .tab-label'));
	const panels = Array.from(tabsWrapper.querySelectorAll('[role="tabpanel"], .tab-panel'));

	// Find the tab for this panel
	const panelId = panel.id;
	let targetTab = null;

	if (panelId) {
		targetTab = tabs.find(t => t.getAttribute('aria-controls') === panelId);
	}

	if (!targetTab) {
		const panelIndex = panels.indexOf(panel);
		if (panelIndex >= 0) {
			targetTab = tabs[panelIndex];
		}
	}

	if (!targetTab) return false;

	// Activate the tab
	tabs.forEach(t => {
		const isActive = t === targetTab;
		t.classList.toggle('active', isActive);
		if (t.hasAttribute('aria-selected')) {
			t.setAttribute('aria-selected', isActive ? 'true' : 'false');
		}
	});

	// Activate the panel — also manage the hidden attribute, which is how panels are actually hidden
	panels.forEach(p => {
		const isActive = p === panel;
		p.classList.toggle('active', isActive);
		if (isActive) {
			p.removeAttribute('hidden');
		} else {
			p.setAttribute('hidden', '');
		}
	});

	return true;
}

/**
 * Scroll to an element with proper offset
 */
function scrollToElement(targetEl, additionalOffset = 20) {
	const headerHeight = getHeaderHeight();
	const elementY = targetEl.getBoundingClientRect().top + window.pageYOffset;
	const offsetY = elementY - headerHeight - additionalOffset;

	window.scrollTo({
		top: Math.max(0, offsetY),
		behavior: 'smooth',
	});
}

/**
 * Handle navigation menu state changes
 */
function closeNavigationMenus() {
	// Close mobile navigation if open
	const navicon = document.querySelector('.site-navigation__navicon--active');
	if (navicon && document.body.classList.contains('site-navigation--open')) {
		document.querySelector('.site-navigation__navicon').click();
	}
}

/**
 * Close submenu if the clicked element is within one
 */
function closeSubMenus(target) {
	// Close sub-menu if active
	if (
		target.parentNode.parentNode &&
		target.parentNode.parentNode.classList.contains('site-navigation__sub-menu')
	) {
		target.parentNode.parentNode.classList.remove('site-navigation__sub-menu--active');
	}

	// Remove active state from parent menu item
	if (
		target.parentNode.parentNode.parentNode &&
		target.parentNode.parentNode.classList.contains('site-navigation__menu-item')
	) {
		const menuItemLink = target.parentNode.parentNode.parentNode.querySelector('a');
		if (menuItemLink) {
			menuItemLink.classList.remove('site-navigation__menu-item--active');
		}
	}
}

/**
 * Handle anchor link clicks with smooth scrolling and proper offset
 */
export function smoothScrollToAnchor(_sel) {
	_sel.addEventListener('click', function (e) {
		const href = e.target.href;

		// Only handle links with anchors
		if (!href || !href.includes('#')) {
			return;
		}

		const [baseUrl, hash] = href.split('#');
		const currentUrl = window.location.href.split('#')[0];

		// If the link is to a different page, let it navigate normally
		if (baseUrl && baseUrl !== currentUrl) {
			return;
		}

		// Prevent default for same-page anchors
		e.preventDefault();

		// Close navigation menus
		closeNavigationMenus();
		closeSubMenus(e.target);

		// Handle empty hash (scroll to top)
		if (!hash || hash.trim() === '') {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
			// Update URL without triggering scroll
			if (window.history.replaceState) {
				window.history.replaceState(null, null, '#');
			}
			return;
		}

		// Try to find the target element by ID
		let targetEl = document.getElementById(hash);

		// If not found, try to find by name attribute (older anchor style)
		if (!targetEl) {
			targetEl = document.querySelector(`[name="${hash}"]`);
		}

		if (targetEl) {
			// Switch to the correct tab if the target is within a tabbed interface
			switchToTabContainingElement(targetEl);

			// Check if the target is an FAQ item and open it
			const faqItem = targetEl.classList.contains('rank-math-list-item')
				? targetEl
				: targetEl.closest('.rank-math-list-item');

			if (faqItem) {
				openFaqItem(faqItem);
			}

			// Small delay to ensure navigation menus are closed, tab is switched, and header height is stable
			setTimeout(() => {
				scrollToElement(targetEl);

				// Update URL without triggering scroll
				if (window.history.replaceState) {
					window.history.replaceState(null, null, `#${hash}`);
				}
			}, 150);
		} else {
			// If element not found, navigate to the URL (might be on another page)
			window.location.href = href;
		}
	});
}

/**
 * Handle anchor links on page load (when URL contains hash)
 */
export function handleInitialHash() {
	const hash = window.location.hash;

	if (hash && hash.length > 1) {
		const targetId = hash.substring(1);

		// Wait for page to be fully loaded and header height to be calculated
		const scrollToHash = () => {
			let targetEl = document.getElementById(targetId);

			// Try name attribute if ID not found
			if (!targetEl) {
				targetEl = document.querySelector(`[name="${targetId}"]`);
			}

			if (targetEl) {
				// Switch to the correct tab if the target is within a tabbed interface
				switchToTabContainingElement(targetEl);

				// Check if the target is an FAQ item and open it
				const faqItem = targetEl.classList.contains('rank-math-list-item')
					? targetEl
					: targetEl.closest('.rank-math-list-item');

				if (faqItem) {
					openFaqItem(faqItem);
				}

				// Short delay to ensure tab is switched and FAQ is opened before scroll position is calculated
				setTimeout(() => {
					scrollToElement(targetEl);
				}, 100);
			}
		};

		// DOM is ready when called from DOMContentLoaded — run immediately
		scrollToHash();
	}
}
